import { Box } from "@mui/material";
import { useMediaSelector } from "../../store/media";
import { useUISelector } from "../../store/UI";
import MediaEntityDetailModal from "./media-entity-modal/MediaEntityDetailModal";

// Intentionally NOT imported from `pages/Map.tsx` to avoid a circular dependency
// (Map.tsx imports MediaDetailOverlay; MediaDetailOverlay used to import
// NAVBAR_HEIGHT back from Map.tsx). With the cycle, HMR could leave
// NAVBAR_HEIGHT undefined for one render and the wrapper would lose its `top`
// offset. Inlined as a string literal here; if NAVBAR_HEIGHT changes, also
// update the Map.tsx export. (Long-term: move both to src/constants/layout.ts.)
const NAVBAR_HEIGHT = "4.1rem";

/**
 * MediaDetailOverlay — Layer B popup renderer.
 *
 * Renders the media carousel (MediaEntityDetailModal) independently of the
 * primary popup (Layer A / EntityMapModal / popupStore).
 *
 * This can be visible at the SAME TIME as any Layer A popup:
 *   geo-entity popup open + media carousel open    → both visible ✓
 *   non-geo-entity popup open + media carousel     → both visible ✓
 *   media-viewer popup open + media carousel       → both visible ✓
 *
 * Trigger: MediaDrawer item click → mediaStore.openMediaDetail(item)
 * Close:   MediaEntityDetailModal calls mediaStore.closeMediaDetail()
 *
 * Layout note:
 * The overlay is wrapped in a position:fixed Box that covers the entire
 * below-navbar viewport. This is deliberate — without it the overlay
 * inherits FlexibleContainer's `maxHeight: calc(100dvh - 11.6625rem)`
 * compact-strip reservation, which leaves an empty gap below the preview
 * and (worse) keeps the overlay inside FlexibleContainer's stacking
 * context where the fullscreen MediaDrawer (position:fixed; zIndex:99)
 * would visually cover it. By placing the overlay in its own viewport-
 * level fixed wrapper, it sits cleanly above any drawer mode and
 * closing the overlay returns the user to whatever mode they were in
 * (e.g. fullscreen grid → preview → close → still fullscreen grid).
 *
 * zIndex chosen to clear the drawer (99) and the bottom-toggle (999) but
 * stay below the calendar slide-down panel (1400) so the timeline UI is
 * always reachable.
 */
const OVERLAY_Z_INDEX = 1000;

export default function MediaDetailOverlay() {
  const { isMediaDetailOpen } = useMediaSelector();
  const { showSidebar, sidebarWidth, dataDrawerMode } = useUISelector();
  // When the media drawer is expanded to fullscreen, the preview should span the
  // full viewport width and ignore the sidebar offset.
  const leftOffset = dataDrawerMode === "fullscreen" ? "0" : showSidebar ? `${sidebarWidth}px` : "0";

  if (!isMediaDetailOpen) return null;

  return (
    <Box
      sx={{
        position: "fixed",
        top: NAVBAR_HEIGHT,
        left: leftOffset,
        right: 0,
        bottom: 0,
        zIndex: OVERLAY_Z_INDEX,
        pointerEvents: "auto",
        transition: "left 0.35s ease"
      }}
      data-testid='media-detail-overlay-wrapper'
    >
      <MediaEntityDetailModal />
    </Box>
  );
}
